/**
 * App Wrapper
 */

import Olark from '../../../../utils/olark';
import './styles.scss';

import * as picturefill from 'picturefill';
import * as React from 'react';
import * as BEM from 'react-bem-helper';
import { connect, DispatchProp } from 'react-redux';

import NotificationManager from '../../../../containers/NotificationManager';
import { OnboardingContext } from '../../../../contexts/onboarding';
import { QueryStringContext } from '../../../../contexts/queryString';
import { hasSearchFilters } from '../../../../contexts/queryString/selectors';
import { IQueryStringState } from '../../../../contexts/queryString/types';
import { setHeaderType } from '../../../../redux/actions/ui';
import { getLicenseUsagesFromQueryString } from '../../../../redux/helpers/license';
import LicenseBar from '../../../molecules/blocks/LicenseBar';
import { RegistrationPagePath } from '../../../pages/account/Registration';
import { BrowsePagePath } from '../../../pages/browse/Browse';
import AddToProjectDrawer from '../../modals/AddToProjectDrawer';
import AuthenticationDrawer from '../../modals/AuthenticationDrawer';
import CookieConsentModal from '../../modals/CookieConsentModal';
import DownloadAgreementModal from '../../modals/DownloadAgreementModal';
import LicenseThisTrackModal from '../../modals/LicenseThisTrackModal';
import TrackDetails from '../../modals/TrackDetails';
import ErrorBoundary from '../ErrorBoundary';
import Header, { ExtendedHeaderProps } from '../Header';
import Player from '../Player';
import AppHelmet from './helmet';

interface IOwnStateProps {
  addToProjectIsVisible: boolean;
  headerIsVisible: boolean;
  headerType: HeaderType;
  licenseBarIsVisible: boolean;
  logInFormIsVisible: boolean;
  playerIsVisible: boolean;
  playerTermListIsVisible: boolean;
  signUpFormIsVisible: boolean;
  trackDetailsSidebarIsVisible: boolean;
  hasUsages: boolean;
  showHeader: boolean;
  showExtendedHeader: boolean;
  path: string;
}

interface IOwnProps extends Pick<ExtendedHeaderProps, 'cartInfo' | 'userProfile'> {
  projects: GQL.IProject[];
  viewer: GQL.IViewer;
  hasFilters?: boolean;
}

interface IMergedProps extends IOwnProps {
  licenseParams: IQueryStringState['license_params'];
}

type AppWrapperProps = IOwnProps & IOwnStateProps & Required<DispatchProp<any>>;

const classes = new BEM('app-wrapper');

class AppWrapper extends React.Component<AppWrapperProps, {}> {
  drawerWrapper = React.createRef<HTMLDivElement>();

  componentDidMount() {
    picturefill();
    if (this.props.showExtendedHeader) {
      this.props.dispatch(setHeaderType({ type: 'extended' }));
    } else {
      this.props.dispatch(setHeaderType({ type: 'simple' }));
    }
    window.addEventListener('scroll', this.onScroll.bind(this));
  }

  componentDidUpdate(prevProps: AppWrapperProps) {
    if (!this.props.playerIsVisible) {
      if (this.hasDrawer()) {
        Olark.hide();
      } else {
        Olark.show();
      }
    } else if (!prevProps.playerIsVisible) {
      Olark.show();
    }

    if (prevProps.path !== this.props.path && this.props.showExtendedHeader) {
      window.scrollTo(0, 0);
      this.props.dispatch(setHeaderType({ type: 'extended' }));
    }

    if (!prevProps.showExtendedHeader && this.props.showExtendedHeader) {
      this.props.dispatch(setHeaderType({ type: 'extended' }));
    }

    if (!this.props.showExtendedHeader && this.props.headerType !== 'simple') {
      window.scrollTo(0, 0);
      this.props.dispatch(setHeaderType({ type: 'simple' }));
    }
  }

  onScroll() {
    if (!this.props.showExtendedHeader) {
      return;
    }
    const headerTransitionY = 375;
    if (window.scrollY >= headerTransitionY && this.props.headerType !== 'hybrid') {
      this.props.dispatch(setHeaderType({ type: 'hybrid' }));
    } else if (window.scrollY < headerTransitionY && this.props.headerType !== 'extended') {
      this.props.dispatch(setHeaderType({ type: 'extended' }));
    }
  }

  render() {
    const {
      addToProjectIsVisible,
      cartInfo,
      children,
      headerIsVisible,
      headerType,
      licenseBarIsVisible,
      logInFormIsVisible,
      playerIsVisible,
      playerTermListIsVisible,
      projects,
      signUpFormIsVisible,
      trackDetailsSidebarIsVisible,
      userProfile,
      showHeader,
      viewer,
    } = this.props;

    return (
      <div
        data-header-visibility={headerIsVisible}
        data-header-type={headerType}
        data-player-visibility={playerIsVisible}
        data-player-term-list-visibility={playerTermListIsVisible}
        data-drawer-visibility={this.hasDrawer()}
        {...classes() }
      >
        <AppHelmet />
        <ErrorBoundary>
          {showHeader ? <Header
            type={headerType}
            cartInfo={cartInfo}
            userProfile={userProfile}
            viewer={viewer}
          /> : null}
          <div {...classes('main')}>
            {licenseBarIsVisible && <LicenseBar />}
            {children}
          </div>
          {playerIsVisible && <Player />}
          <div className="instant-search-wrapper" />
          <div ref={this.drawerWrapper} className="drawer-wrapper">
            {trackDetailsSidebarIsVisible &&
              <TrackDetails currency={viewer!.selected_currency!} cartInfo={cartInfo} />}
            {addToProjectIsVisible && <AddToProjectDrawer projects={projects} />}
            {signUpFormIsVisible && <AuthenticationDrawer action="signup" />}
            {logInFormIsVisible && <AuthenticationDrawer action="login" />}
            <CookieConsentModal />
            <OnboardingContext.Consumer>
              {({ hasNewAchievement }) => !hasNewAchievement() ? (
                <>
                  <LicenseThisTrackModal />
                  <DownloadAgreementModal />
                </>
              ) : null}
            </OnboardingContext.Consumer>
          </div>
        </ErrorBoundary>
        <NotificationManager />
      </div>
    );
  }

  hasDrawer() {
    return this.drawerWrapper.current && this.drawerWrapper.current.childNodes.length > 0;
  }
}

const ConnectedAppWrapper = connect<IOwnStateProps, Required<DispatchProp<any>>, IMergedProps, IStoreState>(
  ({ player, ui, found, staticData: { licenseComponents } }, { licenseParams, hasFilters }) => {
    const licenseUsages = licenseParams ? getLicenseUsagesFromQueryString({
      components: licenseComponents as GQL.ILicenseComponents,
      params: licenseParams,
    }) : [];
    const hasUsages = licenseUsages.length > 0;
    const showHeader = found.match.location.pathname !== RegistrationPagePath;
    const showExtendedHeader = found.match.location.pathname === BrowsePagePath && !hasFilters && !hasUsages;
    return {
      path: found.match.location.pathname,
      showHeader,
      showExtendedHeader,
      hasUsages,
      addToProjectIsVisible: !!ui.addToProject && ui.addToProject.visible,
      headerIsVisible: ui.header.visible,
      headerType: ui.header.type,
      licenseBarIsVisible: !!ui.licenseBar && ui.licenseBar.visible,
      logInFormIsVisible: !!ui.logInForm && ui.logInForm.visible,
      playerIsVisible: player.currentTrack != null,
      playerTermListIsVisible: player.currentTrack != null && !!ui.player && ui.player.termListIsVisible,
      signUpFormIsVisible: !!ui.signUpForm && ui.signUpForm.visible,
      trackDetailsSidebarIsVisible: !!ui.trackDetails && ui.trackDetails.visible,
    };
  },
  (dispatch) => ({ dispatch }),
)(AppWrapper);

const AppWrapperWithQueryStringContext = (props: IOwnProps) => {
  return (
    <QueryStringContext.Consumer>
      {(ctx) => {
        const hasFilters = hasSearchFilters(ctx);
        const licenseParams = ctx.license_params;
        return <ConnectedAppWrapper {...({ hasFilters, licenseParams, ...props })} />;
      }}
    </QueryStringContext.Consumer>
  );
};

export default AppWrapperWithQueryStringContext;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/components/organisms/global/AppWrapper/index.tsx